import React, { useState, useEffect } from 'react';
import api from '../api/axios';

/**
 * Seva reports — dated, sourced, past-tense records of service actually
 * rendered. The public Events page's "Recent Seva & Gatherings" wall reads
 * these when the engine is connected.
 */
const EMPTY = { title: '', date: '', dateLabel: '', place: '', detail: '', sourceUrl: '', published: true };

const SevaReportsManager = () => {
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editId, setEditId] = useState(null);
  const [formData, setFormData] = useState(EMPTY);

  useEffect(() => { fetchReports(); }, []);

  const fetchReports = async () => {
    try {
      const res = await api.get('/seva-reports?all=1');
      setReports(res.data.reports || []);
    } catch (err) {
      console.error('Failed to fetch seva reports', err);
    } finally {
      setLoading(false);
    }
  };

  const openCreate = () => { setEditId(null); setFormData(EMPTY); setShowModal(true); };
  const openEdit = (r) => { setEditId(r._id); setFormData({ ...EMPTY, ...r }); setShowModal(true); };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((f) => ({ ...f, [name]: type === 'checkbox' ? checked : value }));
  };

  const handleSave = async () => {
    if (!formData.title.trim() || !formData.date || !formData.dateLabel.trim()) {
      alert('Title, date and date label are required.');
      return;
    }
    try {
      if (editId) await api.put(`/seva-reports/${editId}`, formData);
      else await api.post('/seva-reports', formData);
      setShowModal(false);
      fetchReports();
    } catch (err) {
      alert(err.response?.data?.message || 'Save failed');
    }
  };

  const handleDelete = async (r) => {
    if (!window.confirm(`Delete "${r.title}"? This cannot be undone.`)) return;
    try {
      await api.delete(`/seva-reports/${r._id}`);
      fetchReports();
    } catch (err) {
      alert(err.response?.data?.message || 'Delete failed');
    }
  };

  const field = 'w-full border border-stone-200 rounded-xl px-4 py-3 text-sm focus:outline-none focus:border-[#2A2858]';

  return (
    <div className="p-8">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold">Seva Reports</h1>
          <p className="text-sm opacity-70">Dated, sourced, past tense — service actually rendered, never an invented calendar.</p>
        </div>
        <button type="button" onClick={openCreate} className="bg-[#2A2858] text-white font-bold px-5 py-3 rounded-xl hover:bg-[#14122B] transition-all shadow-lg active:scale-95">
          + New Report
        </button>
      </div>

      {loading ? (
        <p className="opacity-70">Loading reports…</p>
      ) : reports.length === 0 ? (
        <div className="rounded-xl border border-stone-200 p-8 text-center opacity-80">
          No reports yet. Run <code>node scripts/seed-seva-reports.js</code> in the engine to import the curated wall, or create one above.
        </div>
      ) : (
        <div className="overflow-x-auto rounded-xl border border-stone-200">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left border-b border-stone-200">
                <th className="p-3">When</th>
                <th className="p-3">Title</th>
                <th className="p-3">Place</th>
                <th className="p-3">Source</th>
                <th className="p-3 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {reports.map((r) => (
                <tr key={r._id} className="border-b border-stone-100 last:border-0">
                  <td className="p-3 whitespace-nowrap">{r.dateLabel}</td>
                  <td className="p-3 font-medium">{r.title}</td>
                  <td className="p-3">{r.place}</td>
                  <td className="p-3">
                    {r.sourceUrl
                      ? <a href={r.sourceUrl} target="_blank" rel="noopener noreferrer" className="text-[#2A2858] underline">link</a>
                      : <span className="opacity-50">—</span>}
                  </td>
                  <td className="p-3 text-right whitespace-nowrap">
                    <button type="button" onClick={() => openEdit(r)} className="px-3 py-1 rounded-lg text-xs font-bold bg-stone-100 hover:bg-stone-200 mr-2">Edit</button>
                    <button type="button" onClick={() => handleDelete(r)} className="px-3 py-1 rounded-lg text-xs font-bold bg-red-50 text-red-600 hover:bg-red-100">Delete</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {showModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto p-6">
            <h2 className="text-xl font-bold mb-4">{editId ? 'Edit Report' : 'New Report'}</h2>
            <form onSubmit={(e) => { e.preventDefault(); handleSave(); }} className="space-y-4">
              <input name="title" value={formData.title} onChange={handleChange} placeholder="Title *" className={field} />
              <div className="grid grid-cols-2 gap-4">
                <label className="block text-sm">Anchor date (for sorting) *
                  <input type="date" name="date" value={formData.date} onChange={handleChange} className={field} />
                </label>
                <label className="block text-sm">Shown label * (e.g. 9–13 Sep 2025)
                  <input name="dateLabel" value={formData.dateLabel} onChange={handleChange} className={field} />
                </label>
              </div>
              <input name="place" value={formData.place} onChange={handleChange} placeholder="Place" className={field} />
              <textarea name="detail" value={formData.detail} onChange={handleChange} placeholder="What actually happened — past tense" rows={3} className={field} />
              <input name="sourceUrl" value={formData.sourceUrl} onChange={handleChange} placeholder="Source URL — where this was published" className={field} />
              <label className="flex items-center gap-2 text-sm">
                <input type="checkbox" name="published" checked={formData.published} onChange={handleChange} />
                Published
              </label>
              <div className="pt-4 flex gap-3">
                <button type="button" onClick={() => setShowModal(false)} className="flex-1 bg-stone-100 text-stone-500 font-bold py-3.5 rounded-xl hover:bg-stone-200 transition-all">Discard</button>
                <button type="submit" className="flex-1 bg-[#2A2858] text-white font-bold py-3.5 rounded-xl hover:bg-[#14122B] transition-all shadow-lg active:scale-95">
                  {editId ? 'Update Report' : 'Create Report'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default SevaReportsManager;
